import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection'
import type { NodeKey } from 'lexical'

import { PractitionerPreviewContent } from '~/app/pages/shared/Practitioner/PractitionerPreview'
import { AstraHoverCard, AstraLocationChip, AstraPractitionerChip } from '~/astra'
import { RuleValuesChip } from '~/components/RuleValuesChip'
import { RuleValueChipWrapper } from '~/components/RuleValuesChips'
import { usePractitioner, useResolvedSurgeryTypeGroup } from '~/storm'

import { MentionItem, PractitionerMentionItem, SurgeryTypeMentionItem } from './types'

const UserMentionNodeComponent = ({ mention, mentionCharacter }: { mention: MentionItem; mentionCharacter: string }) => (
    <span className="leading-none text-brand">
        {mentionCharacter}
        {mention.value}
    </span>
)

type PractitionerMentionNodeComponentProps = {
    mention: PractitionerMentionItem
}

const PractionerMentionNodeChip = ({ mention }: PractitionerMentionNodeComponentProps) => (
    <AstraPractitionerChip shortName={mention.value} fullName={mention.fullname} size="small" title={null} />
)

const PractitionerMentionNodeComponent = ({ mention }: { mention: PractitionerMentionItem }) => {
    const practitioner = usePractitioner({ where: { short_name: mention.value }, include: { dipsResource: true } })

    if (practitioner) {
        return (
            <AstraHoverCard noPadding hoverContent={<PractitionerPreviewContent practitioner={practitioner} />}>
                <span>
                    <PractionerMentionNodeChip mention={mention} />
                </span>
            </AstraHoverCard>
        )
    }
    return <PractionerMentionNodeChip mention={mention} />
}

export const SurgeryTypeMentionNodeComponent = ({ mention, disableHover = false }: { mention: SurgeryTypeMentionItem; disableHover?: boolean }) => {
    const surgeryTypeGroup = useResolvedSurgeryTypeGroup(Number(mention.id))
    const groupType = surgeryTypeGroup?.group_type ?? 'none'

    if (disableHover || !surgeryTypeGroup) {
        return <RuleValuesChip variant="filled" groupLevel={groupType} text={mention.value} size="small" />
    }

    return (
        <RuleValueChipWrapper
            resolved={{
                groupLevel: groupType,
                surgeryTypeGroupLabel: surgeryTypeGroup.display_name,
            }}
        >
            <RuleValuesChip variant="filled" groupLevel={groupType} text={mention.value} size="small" />
        </RuleValueChipWrapper>
    )
}

type MentionNodeComponentProps = {
    nodeKey: NodeKey
    mention: MentionItem
    mentionCharacter: string
}

export const MentionNodeComponent = ({ nodeKey, mention, mentionCharacter }: MentionNodeComponentProps) => {
    const [isSelected] = useLexicalNodeSelection(nodeKey)
    const isEditable = useLexicalEditable()
    const mentionType = mention.type

    if (mentionType === 'user') {
        return <UserMentionNodeComponent mention={mention} mentionCharacter={mentionCharacter} />
    }

    return (
        <span className={`inline-flex align-middle ${isSelected ? 'rounded-md border border-ring' : ''}`}>
            {mentionType === 'practitioner' && <PractitionerMentionNodeComponent mention={mention} />}
            {mentionType === 'surgeryType' && <SurgeryTypeMentionNodeComponent mention={mention} disableHover={isEditable} />}
            {mentionType === 'location' && <AstraLocationChip shortName={mention.value} variant="ghost" size="small" />}
        </span>
    )
}
